<template>
  <v-dialog class="modal" v-model="internal">
    <v-card class="pa-5">
      <v-card-actions class="d-flex justify-space-between">
        <v-card-title>{{ $t('importPortModal.title') }}</v-card-title>
        <v-btn icon @click="internal = false"
          ><v-icon small>mdi-close</v-icon></v-btn
        >
      </v-card-actions>
      <div v-if="!mapColumns">
        <v-card-actions class="d-flex flex-column align-start">
          <h4 class="mb-5">{{ $t('importPortModal.fileUpload') }}</h4>
          <div class="upload-files" @click="$refs.fileInput.click()">
            <v-icon large class="mb-2">mdi-upload</v-icon>
            <h3>{{ $t('importPortModal.clickToSelect') }}</h3>
            <h5>{{ $t('importPortModal.fileTypeHint') }}</h5>

            <div class="mt-10 d-flex align-center justify-center">
              <base-button class="mr-2" @click.stop="$refs.fileInput.click()">
               {{ $t('importPortModal.selectFile') }}
              </base-button>
              <input
                type="file"
                ref="fileInput"
                style="display: none"
                accept=".csv, .xlsx"
                @change="handleFile"
              />

              <div class="file-name">
                {{ selectedFile.name }}
              </div>
            </div>
          </div>
        </v-card-actions>

        <v-card-actions
          class="file d-flex align-center w-100 justify-space-around"
        >
          <div class="d-flex justify-center align-center">
            <v-icon large>mdi-file</v-icon>
            <div class="ml-5">
              <h4>{{ selectedFile.name }}</h4>
              <div class="d-flex my-0">
                <p class="mr-2">{{ selectedFile.size }} KB</p>
                •
                <p class="ml-2">{{ selectedFile.noRows }} {{ $t('importPortModal.rows') }}</p>
              </div>
            </div>
          </div>
          <div>
            <v-chip
              medium
              :class="selectedFile.isActive ? 'is-active' : 'is-loading'"
              >{{ selectedFile.isActive ? $t('importPortModal.ready') : $t('extras.loading') }}</v-chip
            >
          </div>
        </v-card-actions>

        <v-divider class="my-5"></v-divider>
        <div class="d-flex justify-end">
          <base-button class="mr-2" @click="internal = false"
            >{{ $t('instructions.cancel') }}</base-button
          >
          <base-button
            isActive
            @click="mapColumns = true"
            :isDisable="!selectedFile.isActive"
            >{{ $t('instructions.next') }}</base-button
          >
        </div>
      </div>
      <div v-else>
        <div class="d-flex flex-row justify-space-around">
          <div>
            <h4 class="mb-4">{{ $t('importPortModal.symbolColumn') }}</h4>
            <v-select
              outlined
              label=""
              :rules="[(v) => !!v || $t('instructions.uploadFiles.obligatory')]"
              :items="requiredItems"
              v-model="selectedOptions.symbolCol"
            >
            </v-select>
          </div>

          <div>
            <h4 class="mb-4">{{ $t('importPortModal.contractsColumn') }}</h4>
            <v-select
              outlined
              label=""
              :rules="[(v) => !!v || $t('instructions.uploadFiles.obligatory')]"
              :items="requiredItems"
              v-model="selectedOptions.contractsCol"
            >
            </v-select>
          </div>

          <div>
            <h4 class="mb-4">{{ $t('importPortModal.aggregationLabelsColumn') }}</h4>
            <v-select
              outlined
              label=""
              :items="requiredItems"
              v-model="selectedOptions.AggregationLabels"
            >
            </v-select>
          </div>
        </div>

        <base-data-table
          class="mx-3"
          :headers="headerItems"
          :items="tableItems[0]?.positions"
          hide-default-footer
        >
          <template slot="item.AggregationLabel" slot-scope="{ item }">
            <span>
              <v-chip class="agg-label">
                {{ item.AggregationLabel }}
              </v-chip>
            </span>
          </template>
        </base-data-table>

        <div class="d-flex justify-end mt-5">
          <base-button class="mr-2" @click="internal = false"
            >{{ $t('instructions.cancel') }}</base-button
          >
          <base-button
            @click="finishImport"
            isActive
            :isDisable="
              !selectedOptions.symbolCol || !selectedOptions.contractsCol
            "
          >
            {{ $t('importPortModal.importSelectedColumns') }}</base-button
          >
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>
<script>
export default {
  name: "ImportModal",
  props: {
    value: { type: Boolean, default: false },
  },
  data() {
    return {
      selectedFile: {
        isActive: false,
        name: "",
        size: "",
        noRows: 0,
      },
      mapColumns: false,
      requiredItems: [],
      headerItems: [
        { text: "A", value: "" },
        { text: "B", value: "" },
        { text: "C", value: "" },
        { text: "D", value: "" },
        { text: "E", value: "" },
      ],
      selectedOptions: {
        symbolCol: null,
        contractsCol: null,
        AggregationLabels: null,
      },
      tableItems: [],
    };
  },
  computed: {
    internal: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      },
    },
  },
  methods: {
    colIndex(letter) {
      if(!letter) return;
      return letter.charCodeAt(0) - 65; // 'A' -> 0, 'B' -> 1, ...
    },
    finishImport() {
      this.tableItems = this.tableItems.map((item) => ({
        ...item,
        positions: item.positions.map((it, idx) => ({
          ...it,
          AggregationLabel:
            Object.values(it)[
              this.colIndex(this.selectedOptions.AggregationLabels)
            ],
          symbol:
            Object.values(it)[this.colIndex(this.selectedOptions.symbolCol)],
          quantity:
            Object.values(it)[this.colIndex(this.selectedOptions.contractsCol)],
        })),
      }));
      this.$emit("table-items", this.tableItems);
      this.internal = false;
    },
    handleFile(e) {
      const file = e.target.files[0];
      if (!file) return;

      if (file) {
        this.selectedFile.isActive = true;
        this.selectedFile.name = file.name;
        this.selectedFile.size = file.size / 1024; //kb
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        const text = event.target.result;
        const id = Math.random().toString(36).substr(2, 9);
        const json = this.csvToJson(text); //json file from csv
        this.tableItems = [
          {
            id,
            name: this.selectedFile.name,
            currency: json[0]?.Currency || "",
            positions: json,
            quantity: json.length,
          },
        ];
      };
      reader.readAsText(file);
    },
    csvToJson(csv) {
      // convert the csv file into json format to save it
      const lines = csv.trim().split("\n");
      this.selectedFile.noRows = lines.length - 1; //-1 because title
      const headers = lines[0].split(",").map((h, i) => ({
        text: String.fromCharCode(65 + i),
        value: h,
      }));
      this.headerItems = headers;
      this.requiredItems = headers.map((h) => ({ text: h.text }));

      return lines.slice(1).map((line, idx) => {
        const cols = line.split(",");
        const obj = {};
        headers.forEach((h, i) => (obj[h.value] = cols[i]?.trim()));
        return obj;
      });
    },
  },
  watch: {
    internal(v) {
      if (!v) {
        //return
        this.mapColumns = false;
        this.selectedFile = {
          isActive: false,
          name: "",
          size: "",
          noRows: 0,
        };
        if (this.$refs.fileInput) {
          this.$refs.fileInput.value = null; // clears file input
        }
      }
    },
  },
};
</script>

<style scoped>

.v-card {
  margin: auto;
  max-width: 70vw !important;
  border-radius: 10px;
}

.file-name {
  border: 1px solid white;
  padding: 0.3rem 1rem;
  width: 60%;
  border-radius: 3px;
}
.upload-files {
  cursor: pointer;
  border: 3px dashed white;
  border-radius: 10px;
  width: 100%;
  padding-top: 3rem;
  padding-bottom: 4rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
}

.upload-files h5 {
  font-weight: 400 !important;
}

.is-active {
  background: rgba(38, 194, 129, 0.4) !important;
  border: 1px solid var(--success) !important;
  font-weight: 700;
  color: var(--success) !important;
}
.agg-label {
  background: rgba(255, 138, 61, 0.4) !important;
  border: 1px solid var(--main) !important;
  color: var(--main) !important;
  font-weight: 700 !important;
}
</style>
